import { useReducedMotion, type Transition } from 'motion/react'
import { motion } from '~/lib/motion'

// Figma "clamable" (Focus Pass Events, node 23900:202695): four diamond
// stars on one 2.95s loop. Each star fades in, shrinks to 12% and rises
// over 0.5593 of the loop, starting at its own offset; between runs it
// holds its first/last keyframe.
const DURATION = 2.95
const FADE_IN = 0.0426
const FADE_OUT = 0.1705
const RISE = 0.5593

const STARS = [
  { left: 21, top: 67, box: 15.556, size: 11, radius: 2, start: 0, rise: -22 },
  {
    left: 65,
    top: 56,
    box: 11.222,
    size: 7.935,
    radius: 2,
    start: 0.1085,
    rise: -20,
  },
  {
    left: 37,
    top: 31,
    box: 11.222,
    size: 7.935,
    radius: 2,
    start: 0.2373,
    rise: -18,
  },
  {
    left: 61,
    top: 19,
    box: 8.389,
    size: 5.932,
    radius: 1.5,
    start: 0.4407,
    rise: -12,
  },
] as const

/** Pads a run starting at `start` with holds out to the loop's 0 and 1. */
function track(start: number, offsets: number[], values: number[]) {
  const times = offsets.map((offset) => start + offset)
  const lead = start > 0
  const trail = times[times.length - 1] < 1
  return {
    values: [
      ...(lead ? [values[0]] : []),
      ...values,
      ...(trail ? [values[values.length - 1]] : []),
    ],
    transition: {
      duration: DURATION,
      times: [...(lead ? [0] : []), ...times, ...(trail ? [1] : [])],
      ease: 'easeInOut',
      repeat: Infinity,
    } satisfies Transition,
  }
}

export function ClaimableBubbles() {
  const reduceMotion = useReducedMotion()
  // Every star rests at opacity 0, so the reduced-motion state is empty.
  if (reduceMotion) return null

  return (
    <div className="pointer-events-none absolute inset-0">
      {STARS.map((star) => {
        const opacity = track(
          star.start,
          [0, FADE_IN, FADE_OUT, RISE],
          [0, 1, 0.76, 0],
        )
        const scale = track(star.start, [0, RISE], [1, 0.12])
        const y = track(star.start, [0, RISE], [0, star.rise])

        return (
          <motion.div
            key={star.start}
            className="absolute flex items-center justify-center"
            style={{
              left: star.left,
              top: star.top,
              width: star.box,
              height: star.box,
            }}
            initial={{ scale: 1, y: 0 }}
            animate={{ scale: scale.values, y: y.values }}
            transition={{ scale: scale.transition, y: y.transition }}
          >
            <div className="flex-none rotate-45">
              <motion.div
                className="bg-white shadow-[0px_0px_30px_0px_rgba(255,255,255,0.4)]"
                style={{
                  width: star.size,
                  height: star.size,
                  borderRadius: star.radius,
                }}
                initial={{ opacity: 0 }}
                animate={{ opacity: opacity.values }}
                transition={opacity.transition}
              />
            </div>
          </motion.div>
        )
      })}
    </div>
  )
}
